import { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, ChevronsUpDown, Columns3, Rows3 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuLabel, DropdownMenuTrigger } from '../ui/dropdown-menu';

const load = (k, d) => { try { return JSON.parse(localStorage.getItem(k)) ?? d; } catch { return d; } };

// columns: [{ key, header, cell?(row), numeric?, sortable?, defaultHidden?, sortValue?(row) }]
export const DataTable = ({ columns, rows, rowKey = 'id', storageKey, sort: ctlSort, onSort, footer, selectable = false, selected = [], onSelectedChange, onRowClick, empty, testId = 'data-table' }) => {
  const sk = storageKey && `ps-table:${storageKey}`;
  const [hidden, setHidden] = useState(() => load(`${sk}:hidden`, columns.filter((c) => c.defaultHidden).map((c) => c.key)));
  const [compact, setCompact] = useState(() => load(`${sk}:compact`, false));
  const [localSort, setLocalSort] = useState(null);
  const sort = onSort ? ctlSort : localSort;
  const setSort = onSort || setLocalSort;
  const persist = (k, v) => sk && localStorage.setItem(`${sk}:${k}`, JSON.stringify(v));
  const toggleCol = (k) => { const n = hidden.includes(k) ? hidden.filter((x) => x !== k) : [...hidden, k]; setHidden(n); persist('hidden', n); };
  const toggleCompact = () => { setCompact(!compact); persist('compact', !compact); };
  const cols = columns.filter((c) => !hidden.includes(c.key));
  const sorted = useMemo(() => {
    if (!sort || onSort) return rows;
    const c = columns.find((x) => x.key === sort.key);
    const val = (r) => (c.sortValue ? c.sortValue(r) : r[c.key]) ?? '';
    return [...rows].sort((a, b) => (val(a) > val(b) ? 1 : val(a) < val(b) ? -1 : 0) * (sort.dir === 'asc' ? 1 : -1));
  }, [rows, sort, columns, onSort]);
  const ids = sorted.map((r) => r[rowKey]);
  const allOn = ids.length > 0 && ids.every((id) => selected.includes(id));
  const flip = (id) => onSelectedChange?.(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  const pad = compact ? 'px-3 py-1.5' : 'px-3 py-2.5';
  return (
    <div className="rounded-lg border border-border bg-card shadow-card" data-testid={testId}>
      <div className="flex items-center justify-end gap-1 border-b border-border px-2 py-1.5">
        {selectable && selected.length > 0 && <span className="mr-auto pl-1 text-xs text-muted-foreground" data-testid={`${testId}-selected-count`}>{selected.length} selected</span>}
        <Button variant="ghost" size="sm" className="h-7 gap-1 text-xs" onClick={toggleCompact} data-testid={`${testId}-compact-toggle`}><Rows3 className="h-3.5 w-3.5" />{compact ? 'Comfortable' : 'Compact'}</Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild><Button variant="ghost" size="sm" className="h-7 gap-1 text-xs" data-testid={`${testId}-columns-btn`}><Columns3 className="h-3.5 w-3.5" />Columns</Button></DropdownMenuTrigger>
          <DropdownMenuContent align="end"><DropdownMenuLabel className="text-xs">Show columns</DropdownMenuLabel>
            {columns.map((c) => <DropdownMenuCheckboxItem key={c.key} checked={!hidden.includes(c.key)} onCheckedChange={() => toggleCol(c.key)} data-testid={`${testId}-col-${c.key}`}>{c.header}</DropdownMenuCheckboxItem>)}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="max-h-[70vh] overflow-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-card">
            <tr className="border-b border-border text-left text-xs font-medium text-muted-foreground">
              {selectable && <th className="w-10 px-3 py-2"><Checkbox checked={allOn} onCheckedChange={() => onSelectedChange?.(allOn ? [] : ids)} aria-label="Select all" data-testid={`${testId}-select-all`} /></th>}
              {cols.map((c) => (
                <th key={c.key} className={cn('px-3 py-2 font-medium', c.numeric && 'text-right')}>
                  {c.sortable ? <button type="button" className="inline-flex items-center gap-1 hover:text-foreground" data-testid={`${testId}-sort-${c.key}`}
                    onClick={() => setSort(sort?.key === c.key && sort.dir === 'asc' ? { key: c.key, dir: 'desc' } : sort?.key === c.key ? null : { key: c.key, dir: 'asc' })}>
                    {c.header}{sort?.key !== c.key ? <ChevronsUpDown className="h-3 w-3" /> : sort.dir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
                  </button> : c.header}
                </th>))}
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 && <tr><td colSpan={cols.length + (selectable ? 1 : 0)} className="p-0">{empty}</td></tr>}
            {sorted.map((r) => (
              <tr key={r[rowKey]} onClick={onRowClick ? () => onRowClick(r) : undefined} data-testid={`${testId}-row-${r[rowKey]}`}
                className={cn('border-b border-border last:border-0 transition-colors ease-ui hover:bg-muted/50', onRowClick && 'cursor-pointer', selected.includes(r[rowKey]) && 'bg-primary/[0.04]')}>
                {selectable && <td className={pad} onClick={(e) => e.stopPropagation()}><Checkbox checked={selected.includes(r[rowKey])} onCheckedChange={() => flip(r[rowKey])} aria-label="Select row" data-testid={`${testId}-select-${r[rowKey]}`} /></td>}
                {cols.map((c) => <td key={c.key} className={cn(pad, c.numeric && 'text-right tabular')}>{c.cell ? c.cell(r) : r[c.key]}</td>)}
              </tr>))}
          </tbody>
        </table>
      </div>
      {footer}
    </div>
  );
};
